import { all, takeLatest, call, put, select } from 'redux-saga/effects'
import SeedCropActions from 'modules/domain/seedCrop/duck'
import { push } from 'connected-react-router'
import { generatePath } from 'react-router-dom'
import * as managers from './managers'
import SeedCropSelectors from './selectors'
import { SeedCrop } from 'modules/domain/seedCrop/types'
import SeedCropRoutes from 'views/pages/SeedCrop/routes'
import { ListResponse } from 'types/api'
import { updateLocationQuery } from 'modules/sagaHelpers'
import { RequestError } from 'modules/errors'

export const fetchList = function* () {
  try {
    let currentPage = yield select(SeedCropSelectors.page)
    const filter = yield select(SeedCropSelectors.filter)
    const sorting = yield select(SeedCropSelectors.sorting)
    const pageSize = yield select(SeedCropSelectors.pageSize)

    let response: ListResponse<SeedCrop> = yield call(managers.getSeedCropsList, filter, sorting, currentPage, pageSize)
    const pages = Math.ceil(response.total_count / pageSize)
    if (pages !== 0 && pages < currentPage) {
      response = yield call(managers.getSeedCropsList, filter, sorting, pages, pageSize)
      currentPage = pages
    }

    const { data, page, total_count } = response
    yield put(SeedCropActions.listRequestSucceed(data, total_count, page))
    yield call(updateLocationQuery, SeedCropRoutes.List, { page: currentPage })
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.listRequestFailed(errType))
  }
}

export const fetchListNext = function* () {
  try {
    const page = yield select(SeedCropSelectors.page)
    const filter = yield select(SeedCropSelectors.filter)
    const sorting = yield select(SeedCropSelectors.sorting)
    const pageSize = yield select(SeedCropSelectors.pageSize)
    const { data, total_count }: { data: SeedCrop[]; total_count: number } = yield call(
      managers.getSeedCropsList,
      filter,
      sorting,
      page,
      pageSize,
    )
    yield put(SeedCropActions.listRequestNextSucceed(data, total_count))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.listRequestNextFailed(errType))
  }
}

export const fetchSeedCrop = function* ({ payload: id }: ReturnType<typeof SeedCropActions.itemRequested>) {
  try {
    const seedCrop: SeedCrop = yield call(managers.getSeedCrop, id)
    yield put(SeedCropActions.itemRequestSucceed(seedCrop))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.itemRequestFailed(id, errType))
  }
}

export const addSeedCrop = function* ({ payload: dto }: ReturnType<typeof SeedCropActions.addRequested>) {
  try {
    const seedCrop: SeedCrop = yield call(managers.addSeedCrop, dto)
    yield put(SeedCropActions.addSucceed(seedCrop))
    yield put(push(generatePath(SeedCropRoutes.Edit, { id: seedCrop.id })))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.addFailed(errType))
  }
}

export const updateSeedCrop = function* ({ payload: [id, dto] }: ReturnType<typeof SeedCropActions.updateRequested>) {
  try {
    const seedCrop: SeedCrop = yield call(managers.updateSeedCrop, id, dto)
    yield put(SeedCropActions.updateSucceed(seedCrop))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.updateFailed(id, errType))
  }
}

export const removeSeedCrop = function* ({ payload }: ReturnType<typeof SeedCropActions.removeRequested>) {
  try {
    yield call(managers.removeSeedCrop, payload)
    yield put(SeedCropActions.removeSucceed(payload))
    yield put(push(SeedCropRoutes.List))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedCropActions.removeFailed(payload, errType))
  }
}

const SeedCropSagas = function* () {
  yield all([
    takeLatest(SeedCropActions.itemRequested.type, fetchSeedCrop),

    takeLatest(SeedCropActions.listRequested.type, fetchList),
    takeLatest(SeedCropActions.filterUpdated.type, fetchList),
    takeLatest(SeedCropActions.sortingUpdated.type, fetchList),
    takeLatest(SeedCropActions.filterHasBeenReset.type, fetchList),
    takeLatest(SeedCropActions.sortingHasBeenReset.type, fetchList),

    takeLatest(SeedCropActions.listRequestedNext.type, fetchListNext),

    takeLatest(SeedCropActions.addRequested.type, addSeedCrop),
    takeLatest(SeedCropActions.updateRequested.type, updateSeedCrop),
    takeLatest(SeedCropActions.removeRequested.type, removeSeedCrop),
  ])
}

export default SeedCropSagas
